import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useMutation } from '@tanstack/react-query';
import {
  Record,
  RecordType,
  RecordCreateInput,
  Vehicle,
  SUPPORTED_FILE_TYPES,
  MAX_FILE_SIZE,
} from '@vehicle-logbook/shared';
import axios from 'axios';
import { toast } from 'react-toastify';
import { X, Upload, File as FileIcon, Trash2 } from 'lucide-react';
import { formatDateTimeLocal } from '../../utils/dateFormat';
import { Modal, ModalBody, ModalFooter } from '../common/Modal';
import { FormField } from '../common/FormField';
import {
  btnPrimary,
  btnSecondary,
  fieldGridClass,
  inputClass,
  inputGroupLeftClass,
  inputGroupRightClass,
  selectClass,
  textareaClass,
  hintClass,
} from '../common/formStyles';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

interface RecordFormProps {
  record?: Record | null;
  vehicles: Vehicle[];
  onClose: () => void;
  onSuccess: () => void;
}

export const RecordForm = ({
  record,
  vehicles,
  onClose,
  onSuccess,
}: RecordFormProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [formData, setFormData] = useState<RecordCreateInput>({
    vehicleId: '',
    date: new Date(),
    mileage: 0,
    type: 'maintenance',
    title: '',
    description: '',
    cost: 0,
    currency: 'UAH',
    location: '',
    tags: [],
  });

  useEffect(() => {
    if (record) {
      let date: Date;
      try {
        if (record.date?.toDate && typeof record.date.toDate === 'function') {
          date = record.date.toDate();
        } else if (record.date instanceof Date) {
          date = record.date;
        } else if (
          typeof record.date === 'string' ||
          typeof record.date === 'number'
        ) {
          date = new Date(record.date);
        } else if (record.date?.seconds) {
          date = new Date(record.date.seconds * 1000);
        } else {
          date = new Date();
        }
        if (isNaN(date.getTime())) {
          date = new Date();
        }
      } catch (error) {
        date = new Date();
      }
      setFormData({
        vehicleId: record.vehicleId,
        date,
        mileage: record.mileage,
        type: record.type,
        title: record.title,
        description: record.description,
        cost: record.cost,
        currency: record.currency,
        location: record.location || '',
        tags: record.tags || [],
      });
      setAttachments(record.attachments || []);
    } else if (vehicles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        vehicleId: vehicles[0].id,
        mileage: vehicles[0].currentMileage,
      }));
    }
  }, [record, vehicles]);

  const createMutation = useMutation({
    mutationFn: async (data: RecordCreateInput) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const response = await axios.post(`${API_URL}/records`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('record.addSuccess'));
      onSuccess();
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || t('errors.generic');
      toast.error(errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (
      data: Partial<RecordCreateInput> & { attachments?: string[] }
    ) => {
      if (!user || !record) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const response = await axios.put(
        `${API_URL}/records/${record.id}`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('record.updateSuccess'));
      onSuccess();
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || t('errors.generic');
      toast.error(errorMessage);
    },
  });

  const uploadFileMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const response = await axios.post(`${API_URL}/upload`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data.url;
    },
    onSuccess: (url) => {
      setAttachments((prev) => [...prev, url]);
      toast.success(t('record.fileUploaded'));
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || t('errors.generic');
      toast.error(errorMessage);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let dateValue: Date;
    if (formData.date instanceof Date) {
      dateValue = formData.date;
    } else if (
      formData.date &&
      typeof (formData.date as any).toDate === 'function'
    ) {
      dateValue = (formData.date as any).toDate();
    } else {
      dateValue = new Date();
    }
    const data = {
      ...formData,
      date: dateValue,
      location: formData.location || undefined,
      tags: formData.tags || [],
      attachments: attachments,
    };

    if (record) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
      toast.error(t('record.invalidFileType'));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t('record.fileTooLarge'));
      return;
    }

    setUploadingFiles(true);
    try {
      await uploadFileMutation.mutateAsync(file);
    } finally {
      setUploadingFiles(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = (url: string) => {
    setAttachments((prev) => prev.filter((a) => a !== url));
  };

  const handleTagAdd = (tag: string) => {
    if (tag.trim() && !formData.tags?.includes(tag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tag.trim()],
      }));
    }
  };

  const handleTagRemove = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags?.filter((t) => t !== tag) || [],
    }));
  };

  const recordTypes: RecordType[] = [
    'maintenance',
    'repair',
    'fuel',
    'parts',
    'other',
  ];

  const currencies = ['UAH', 'USD', 'EUR', 'GBP'];

  const selectedVehicle = vehicles.find((v) => v.id === formData.vehicleId);

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const mileageHint = selectedVehicle
    ? `${t('vehicle.currentMileage')}: ${selectedVehicle.currentMileage.toLocaleString()} ${t('vehicle.km')}`
    : undefined;

  return (
    <Modal
      title={record ? t('record.edit') : t('record.add')}
      onClose={onClose}
      size="3xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody>
          <div className={fieldGridClass}>
            <FormField label={t('vehicle.title')} required>
              <select
                required
                value={formData.vehicleId}
                onChange={(e) =>
                  setFormData({ ...formData, vehicleId: e.target.value })
                }
                className={selectClass}
              >
                <option value="">{t('common.select')}</option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label={t('record.date')} required>
              <input
                type="datetime-local"
                required
                value={formatDateTimeLocal(formData.date)}
                onChange={(e) =>
                  setFormData({ ...formData, date: new Date(e.target.value) })
                }
                className={inputClass}
              />
            </FormField>

            <FormField label={t('record.type')} required>
              <select
                required
                value={formData.type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    type: e.target.value as RecordType,
                  })
                }
                className={selectClass}
              >
                {recordTypes.map((type) => (
                  <option key={type} value={type}>
                    {t(`record.types.${type}`)}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label={t('record.mileage')} required hint={mileageHint}>
              <input
                type="number"
                required
                min="0"
                step="1"
                value={formData.mileage}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    mileage: parseFloat(e.target.value) || 0,
                  })
                }
                className={inputClass}
              />
            </FormField>

            <FormField label={t('record.title')} required>
              <input
                type="text"
                required
                maxLength={200}
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className={inputClass}
              />
            </FormField>

            <FormField label={t('record.cost')} required>
              <div className="flex w-full">
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      cost: parseFloat(e.target.value) || 0,
                    })
                  }
                  className={inputGroupLeftClass}
                />
                <select
                  value={formData.currency}
                  onChange={(e) =>
                    setFormData({ ...formData, currency: e.target.value })
                  }
                  className={inputGroupRightClass}
                >
                  {currencies.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </div>
            </FormField>

            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t('record.description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
                className={textareaClass}
              />
            </div>

            <FormField label={t('record.location')}>
              <input
                type="text"
                value={formData.location}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
                className={inputClass}
              />
            </FormField>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t('record.tags')}
              </label>
              <input
                type="text"
                placeholder={t('record.addTag')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleTagAdd(e.currentTarget.value);
                    e.currentTarget.value = '';
                  }
                }}
                className={inputClass}
              />
              <div className={`${hintClass} flex flex-wrap gap-1.5`}>
                {formData.tags?.length
                  ? formData.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleTagRemove(tag)}
                          className="ml-1 text-primary-600 hover:text-primary-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))
                  : '\u00A0'}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t('record.attachments')}
              </label>
              {attachments.length > 0 && (
                <div className="mb-2 grid grid-cols-2 gap-2 md:grid-cols-4">
                  {attachments.map((url) => (
                    <div
                      key={url}
                      className="group relative rounded-lg border border-gray-200 p-2 transition-colors hover:border-primary-500"
                    >
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <FileIcon className="mx-auto mb-1 h-7 w-7 text-gray-400" />
                        <p className="truncate text-center text-xs text-gray-600">
                          {url.split('/').pop()?.substring(0, 15)}...
                        </p>
                      </a>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(url)}
                        className="absolute right-1 top-1 rounded-full bg-red-500 p-1 text-white opacity-0 transition-opacity hover:bg-red-600 group-hover:opacity-100"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="cursor-pointer rounded-lg border-2 border-dashed border-gray-300 p-3 text-center transition-colors hover:border-primary-500"
              >
                <Upload className="mx-auto mb-1 h-6 w-6 text-gray-400" />
                <p className="text-sm text-gray-600">
                  {uploadingFiles
                    ? t('common.loading')
                    : t('record.clickToUpload')}
                </p>
                <p className="text-xs text-gray-500">
                  {t('record.fileFormat')}
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={SUPPORTED_FILE_TYPES.join(',')}
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploadingFiles}
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <button type="button" onClick={onClose} className={btnSecondary}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={isLoading} className={btnPrimary}>
            {isLoading ? t('common.loading') : t('common.save')}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
